import App from 'next/app';
import React from 'react';
import { get } from 'lodash';

import { ApolloProvider } from '@apollo/react-common';
import * as Sentry from '@sentry/browser';

import { DesignSystemProvider } from '@zapier/design-system';
import { LinkProvider } from '@zapier/common-hooks';
import { ScrollToTop, StaticAssetProvider } from '@zapier/common-components';
import { TrackingProvider, TrackingCookies } from '@zapier/toolbox-tracking';
import { isBrowser } from '@zapier/common-utils';
import { UserAgentContext } from '@zapier/responsive-utils';

import FullStory from '../fullstory/FullStory';
import buildStaticAssetPath from '../utils/buildStaticAssetPath';
import initializeTracking from '../tracking/initializeTracking';
import TrackRouting from '../tracking/TrackRouting';
import maybeSEOFallback from '../utils/maybeSEOFallback';
import trackingFunctions from '../tracking/trackingFunctions';
import withApolloClient from '../apollo/withApolloClient';
import { initializeNprogress } from '../loadingIndicator/nprogress';
import initializeDatadogRum from '../realUserMonitoring/initializeDatadogRum';
import { setFrontendAppConfig } from '../config/frontendAppConfig';
import { Link, initializeNextRoutes, pushRoute } from '../routes/routes';

const appPage = (CustomAppComponent, config) => {
  setFrontendAppConfig(config);
  initializeNextRoutes(config.routes);
  initializeDatadogRum(config);
  initializeTracking(config);
  initializeNprogress();

  class AppPage extends App {
    static displayName = `appPage(${CustomAppComponent.name})`;

    static async getInitialProps(appContext) {
      const { Component, ctx } = appContext;

      // If the custom App component provides its own `getInitialProps`, it is
      // responsible for in turn calling the `getInitialProps` for individual
      // page components, and returning them under the `pageProps` key.
      // Otherwise, we will handle it automatically.
      const initialProps =
        typeof CustomAppComponent.getInitialProps === 'function'
          ? await CustomAppComponent.getInitialProps(appContext)
          : Component.getInitialProps
          ? { pageProps: await Component.getInitialProps(ctx) }
          : { pageProps: {} };

      // A fallback user-agent string must be returned if a user-agent string
      // does not exist in the incoming HTTP request, otherwise the return value
      // from this function will be serialized into the cached page (as part of
      // __NEXT_DATA__) and eventually trigger a JS error in client-side code
      // which always expects a UA string.
      const userAgent = isBrowser()
        ? navigator.userAgent
        : ctx.req.headers['user-agent'] ||
          'Mozilla/5.0 (Macintosh; Intel Mac OS X x.y; rv:10.0) Gecko/20100101 Firefox/10.0';

      if (get(ctx, ['res', 'statusCode']) === 404) {
        await maybeSEOFallback(ctx);
      }

      return { ...initialProps, userAgent };
    }

    componentDidMount() {
      if (isBrowser()) {
        // Stores any UTM or click ID params in the query string in a cookie
        TrackingCookies.storeUtms();
        TrackingCookies.storeAllClickIds();
      }
    }

    componentDidCatch(error, errorInfo) {
      Sentry.withScope(scope => {
        scope.setExtra('details', {
          extra: {
            ...errorInfo,
            source: 'ssr',
          },
        });
        Sentry.captureException(error);
      });

      // Without this, the error overlay in development and the `_error.js`
      // page in production will not be rendered.
      super.componentDidCatch(error, errorInfo);
    }

    render() {
      const { router } = this.props;

      return (
        <ApolloProvider client={this.props.apolloClient}>
          <DesignSystemProvider LinkComponent={Link}>
            <StaticAssetProvider staticAssetPath={buildStaticAssetPath('')}>
              <LinkProvider routeTo={pushRoute}>
                <TrackingProvider trackingFunctions={trackingFunctions}>
                  <TrackRouting path={router.asPath}>
                    <ScrollToTop
                      // Don't scroll to the top of the page if the client is trying to
                      // view a link which contains an anchor
                      shouldScrollUp={!router.asPath.includes('#')}
                      value={router.asPath}
                    >
                      <UserAgentContext.Provider value={this.props.userAgent}>
                        <CustomAppComponent {...this.props} />
                      </UserAgentContext.Provider>
                      <FullStory />
                    </ScrollToTop>
                  </TrackRouting>
                </TrackingProvider>
              </LinkProvider>
            </StaticAssetProvider>
          </DesignSystemProvider>
        </ApolloProvider>
      );
    }
  }

  return withApolloClient(AppPage, config);
};

export default appPage;
